import { admissionState, institutions, portalLink } from './admissionRouting';
import { useState, useRef } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useResource } from "hooks/useResource";
import { getAdmissionRounds } from "services/content";
import { startApplication, saveDraftAccess } from "services/admissions";
import {
  PortalShell,
  Surface,
  Button,
  Field,
  Select,
  ErrorSummary,
  LangToggle,
  useT,
  useFontClass,
} from "components/admissions/portalUi";

/**
 * "Start with the correct admission route" — the first thing a family chooses
 * is the class, and the group only appears when that class actually has one.
 * Everything downstream (which sections show, which subjects are offered) keys
 * off this, so it is asked once, first, from the live round.
 */
export default function PortalStart() {
  const [query] = useSearchParams();
  const institution = query.get("institution");
  const startToken = useRef(null);
  const t = useT();
  const font = useFontClass();
  const navigate = useNavigate();
  const { data: rounds, loading, error: roundError } = useResource(getAdmissionRounds);

  const round = (rounds || []).find((r) => r.institution === institution);
  const isOpen = admissionState(round) === "open";
  const classes = round?.classes || [];

  const [classApplied, setClassApplied] = useState("");
  const [groupApplied, setGroupApplied] = useState("");
  const [errors, setErrors] = useState([]);
  const [busy, setBusy] = useState(false);

  const chosen = classes.find((c) => c.class === classApplied);
  const groups = chosen?.groups || [];
  const errFor = (id) => errors.find((e) => e.id === id)?.message;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!isOpen) { setErrors([{ id: "classApplied", message: t("আবেদন এখন বন্ধ।", "Applications are not currently open.") }]); return; }
    const found = [];
    if (!classApplied)
      found.push({
        id: "classApplied",
        message: t("কোন শ্রেণিতে আবেদন করবেন বেছে নিন", "Choose the class you are applying for"),
      });
    if (groups.length && !groupApplied)
      found.push({ id: "groupApplied", message: t("বিভাগ বেছে নিন", "Choose a group") });
    setErrors(found);
    if (found.length) return;

    setBusy(true);
    try {
      if (!startToken.current) startToken.current = Array.from(window.crypto.getRandomValues(new Uint8Array(32)), b => b.toString(16).padStart(2, '0')).join('');
      const started = await startApplication({ institution, classApplied, groupApplied, startToken: startToken.current });
      saveDraftAccess(started);
      navigate("/admissions-portal/section");
    } catch (err) {
      // The server is the authority on whether the round is open and whether
      // this class is offered; surface its wording rather than guessing.
      setErrors([{ id: "classApplied", message: err.message }]);
    } finally {
      setBusy(false);
    }
  };

  if (!institutions[institution]) return <PortalShell><Link to="/admissions-portal">{t('প্রতিষ্ঠান বেছে নিন', 'Choose your institution')}</Link></PortalShell>;
  return (
    <PortalShell>
      <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
        <Link
          to={portalLink(institution)}
          className={`${font} text-[14px] text-muted no-underline hover:text-ink min-h-[44px] inline-flex items-center`}
        >
          ← {t("ফিরে যান", "Back")}
        </Link>
        <LangToggle />
      </div>

      <h1
        className={`${font} font-serif text-[28px] sm:text-[34px] font-bold text-green-dark leading-tight mb-2`}
      >
        {t("কোন শ্রেণিতে আবেদন?", "Which class are you applying for?")}
      </h1>
      <p className={`${font} text-[16px] text-muted leading-relaxed mb-6`}>
        {t(
          "আপনার উত্তর অনুযায়ী পরের ধাপগুলো ঠিক হবে — অপ্রয়োজনীয় প্রশ্ন দেখানো হবে না।",
          "The rest of the form adapts to this answer, so you will not be asked anything that does not apply."
        )}
      </p>

      <p className="mb-4 font-semibold">{institutions[institution]} · {round?.academicYear}</p><ErrorSummary errors={roundError ? [{id: "classApplied", message: "Could not load admission details."}] : errors} className="mb-6" />

      <form onSubmit={onSubmit} noValidate>
        <Surface className="p-5 sm:p-7 mb-6">
          {loading ? (
            <span className={`${font} text-[15px] text-muted`}>{t("লোড হচ্ছে…", "Loading…")}</span>
          ) : !isOpen || !classes.length ? (
            <p className={`${font} text-[16px] text-muted`}>
              {t(
                "এই মুহূর্তে কোনো শ্রেণিতে আবেদন নেওয়া হচ্ছে না।",
                "No classes are accepting applications right now."
              )}
            </p>
          ) : (
            <div className="flex flex-col gap-5">
              <Field id="classApplied" label={t("শ্রেণি", "Class")} required error={errFor("classApplied")}>
                <Select
                  id="classApplied"
                  value={classApplied}
                  onChange={(e) => {
                    setClassApplied(e.target.value);
                    setGroupApplied("");
                  }}
                  error={errFor("classApplied")}
                >
                  <option value="">{t("বেছে নিন…", "Choose…")}</option>
                  {classes.map((c) => (
                    <option key={c.class} value={c.class}>
                      {c.class}
                      {c.fee ? ` — ${c.fee}` : ""}
                    </option>
                  ))}
                </Select>
              </Field>

              {/* Only shown when the chosen class actually has groups. */}
              {groups.length > 0 && (
                <Field id="groupApplied" label={t("বিভাগ", "Group")} required error={errFor("groupApplied")}>
                  <Select
                    id="groupApplied"
                    value={groupApplied}
                    onChange={(e) => setGroupApplied(e.target.value)}
                    error={errFor("groupApplied")}
                  >
                    <option value="">{t("বেছে নিন…", "Choose…")}</option>
                    {groups.map((g) => (
                      <option key={g} value={g}>
                        {g}
                      </option>
                    ))}
                  </Select>
                </Field>
              )}
            </div>
          )}
        </Surface>

        <Button type="submit" disabled={!isOpen || busy || !classes.length} className="w-full sm:w-auto">
          {busy ? t("শুরু হচ্ছে…", "Starting…") : t("আবেদন শুরু করুন", "Start application")}
        </Button>

        <p className={`${font} text-[14px] text-muted mt-4 leading-relaxed`}>
          {t(
            "শুরু করলেই একটি আবেদন নম্বর তৈরি হবে। নম্বরটি সংরক্ষণ করুন — পরে ফিরে আসতে এটি লাগবে।",
            "Starting creates your application number. Keep it safe — you will need it to come back."
          )}
        </p>
      </form>
    </PortalShell>
  );
}
